class CollectionFilters {
  constructor(section) {
    this.section = section;
    this.drawer = section.querySelector('[data-filter-drawer]');
    this.openButton = section.querySelector('[data-filter-open]');
    this.closeButton = section.querySelector('[data-filter-close]');
    this.overlay = section.querySelector('[data-filter-overlay]');
    this.sortSelect = section.querySelector('[data-sort-select]');
    this.desktopQuery = window.matchMedia('(min-width: 64rem)');
    this.lastFocusedElement = null;

    this.onKeydown = this.onKeydown.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.bindEvents();
    this.onViewportChange();
  }

  bindEvents() {
    this.openButton?.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.sortSelect?.addEventListener('change', () => {
      const form = document.getElementById(this.sortSelect.getAttribute('form'));
      if (!form) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
    this.section.addEventListener('keydown', this.onKeydown);
    if (typeof this.desktopQuery.addEventListener === 'function') {
      this.desktopQuery.addEventListener('change', this.onViewportChange);
    } else {
      this.desktopQuery.addListener(this.onViewportChange);
    }
  }

  open() {
    if (!this.drawer || !this.openButton || !this.overlay) return;

    this.lastFocusedElement = document.activeElement;
    this.drawer.classList.add('is-open');
    this.drawer.removeAttribute('inert');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.setAttribute('role', 'dialog');
    this.drawer.setAttribute('aria-modal', 'true');
    this.openButton.setAttribute('aria-expanded', 'true');
    this.overlay.hidden = false;
    document.body.classList.add('filters-open');
    this.closeButton?.focus();
  }

  close({ restoreFocus = true } = {}) {
    if (!this.drawer || !this.openButton || !this.overlay) return;

    this.drawer.classList.remove('is-open');
    this.openButton.setAttribute('aria-expanded', 'false');
    this.overlay.hidden = true;
    document.body.classList.remove('filters-open');

    if (!this.desktopQuery.matches) {
      this.drawer.setAttribute('inert', '');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.removeAttribute('role');
      this.drawer.removeAttribute('aria-modal');
    }

    if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }
  }

  trapFocus(event) {
    const focusable = [
      ...this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((element) => element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onKeydown(event) {
    if (!this.drawer?.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  onViewportChange() {
    if (!this.drawer) return;

    if (this.desktopQuery.matches) {
      this.drawer.classList.remove('is-open');
      this.drawer.removeAttribute('inert');
      this.drawer.removeAttribute('aria-hidden');
      this.drawer.removeAttribute('role');
      this.drawer.removeAttribute('aria-modal');
      this.openButton?.setAttribute('aria-expanded', 'false');
      if (this.overlay) this.overlay.hidden = true;
      document.body.classList.remove('filters-open');
    } else {
      this.close({ restoreFocus: false });
    }
  }

  destroy() {
    if (typeof this.desktopQuery.removeEventListener === 'function') {
      this.desktopQuery.removeEventListener('change', this.onViewportChange);
    } else {
      this.desktopQuery.removeListener(this.onViewportChange);
    }
    document.body.classList.remove('filters-open');
  }
}

const initializeCollectionFilters = (scope = document) => {
  scope
    .querySelectorAll('[data-collection-section]:not([data-collection-initialized])')
    .forEach((section) => {
      section.dataset.collectionInitialized = 'true';
      section.collectionFiltersInstance = new CollectionFilters(section);
    });
};

initializeCollectionFilters();

document.addEventListener('shopify:section:load', (event) => {
  initializeCollectionFilters(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelector('[data-collection-section]')?.collectionFiltersInstance?.destroy();
});
